import { useState, useEffect, useCallback } from 'react'
import type { AppCore } from '@core/bootstrap'
import type { CorrectionKind, SaleReceipt, SettlementType } from '@core/db/models'
import type { AdminSectionProps } from './types'
import { SETTLEMENT_LABELS } from './shared'
import { money } from '@core/utils/format'
import { hint, table, tableWrap, td, tdNum, th, thNum } from '@ui/classes'
import { credInput, eqBtn, eqFieldLabel, errBanner, okBanner } from '@ui/admin'
import { AdminCard } from './AdminCard'
import { Check, Clock } from '@ui/icons'

export function CorrectionsSection({
  core,
  canCorrect,
  cashierName
}: {
  core: AppCore
  canCorrect: boolean
  cashierName?: string
}) {
  const today = new Date().toISOString().slice(0, 10)
  const [form, setForm] = useState({
    settlementType: 'income' as SettlementType,
    kind: 'self' as CorrectionKind,
    basis: '',
    baseDate: today,
    baseNumber: '',
    instructionDate: '',
    instructionNumber: '',
    taxPeriod: '',
    total: '',
    paymentMethod: 'cash' as 'cash' | 'electronic'
  })
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [items, setItems] = useState<SaleReceipt[]>([])

  const refresh = useCallback(async () => {
    setItems(await core.correction.listCorrections(20))
  }, [core.correction])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setMessage(null)

    const total = Number(form.total.replace(',', '.'))
    if (!Number.isFinite(total) || total <= 0) {
      setError('Укажите сумму больше нуля')
      return
    }
    if (!form.basis.trim() || !form.baseDate.trim() || !form.baseNumber.trim()) {
      setError('Заполните основание, дату и номер документа')
      return
    }
    if (
      form.kind === 'instruction' &&
      (!form.instructionDate.trim() || !form.instructionNumber.trim())
    ) {
      setError('Для коррекции по предписанию укажите дату и номер предписания')
      return
    }

    const confirmed = window.confirm(
      `Пробить чек коррекции?\n${SETTLEMENT_LABELS[form.settlementType]} · ` +
        `${money(total)} · ${form.paymentMethod === 'cash' ? 'наличные' : 'безнал'}`
    )
    if (!confirmed) return

    setBusy(true)
    try {
      const result = await core.correction.createCorrection({
        settlementType: form.settlementType,
        correction: {
          kind: form.kind,
          basis: form.basis.trim(),
          baseDate: form.baseDate,
          baseNumber: form.baseNumber.trim(),
          instructionDate: form.kind === 'instruction' ? form.instructionDate : undefined,
          instructionNumber:
            form.kind === 'instruction' ? form.instructionNumber.trim() : undefined,
          taxPeriod: form.kind === 'instruction' ? form.taxPeriod.trim() || undefined : undefined
        },
        total,
        paymentMethod: form.paymentMethod,
        cashierName
      })

      if (!result.ok) {
        setError(result.error ?? 'Не удалось создать чек коррекции')
      } else if (result.status === 'fiscalized' && result.fiscalData) {
        setMessage(
          `Чек коррекции пробит: ФД №${result.fiscalData.fiscalDocumentNumber} · ` +
            `ФПД ${result.fiscalData.fiscalDocumentSign}`
        )
        setForm((f) => ({ ...f, basis: '', baseNumber: '', total: '' }))
      } else {
        setMessage(
          'ККТ недоступна — коррекция отложена до восстановления связи'
        )
        setForm((f) => ({ ...f, basis: '', baseNumber: '', total: '' }))
      }
      await refresh()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  // Без права can_correction раздел не показываем вовсе.
  if (!canCorrect) return null

  return (
    <AdminCard id="documents.corrections" title="Коррекции (ФЗ-54)" defaultOpen>
      <p className={hint}>
        Чек коррекции формируется по ФФД: указываются признак расчёта, основание и документ.
        Коррекция всегда фискальная — при недоступной ККТ она уходит в очередь пробития на ККТ.
      </p>

      {message && <p className={okBanner}>{message}</p>}
      {error && <p className={errBanner}>{error}</p>}

      <form onSubmit={submit} className="flex flex-wrap gap-2.5">
        <label className={eqFieldLabel}>
          Тип расчёта
          <select
            value={form.settlementType}
            onChange={(e) =>
              setForm((f) => ({ ...f, settlementType: e.target.value as SettlementType }))
            }
            className={credInput}
          >
            {(Object.keys(SETTLEMENT_LABELS) as SettlementType[]).map((key) => (
              <option key={key} value={key}>
                {SETTLEMENT_LABELS[key]}
              </option>
            ))}
          </select>
        </label>

        <label className={eqFieldLabel}>
          Тип коррекции
          <span className="flex min-h-8 items-center gap-2.5">
            <label className="flex items-center gap-1 font-normal">
              <input
                type="radio"
                name="correction-kind"
                checked={form.kind === 'self'}
                onChange={() => setForm((f) => ({ ...f, kind: 'self' }))}
               className="h-4 w-4 cursor-pointer accent-ok"/>
              Самостоятельная
            </label>
            <label className="flex items-center gap-1 font-normal">
              <input
                type="radio"
                name="correction-kind"
                checked={form.kind === 'instruction'}
                onChange={() => setForm((f) => ({ ...f, kind: 'instruction' }))}
               className="h-4 w-4 cursor-pointer accent-ok"/>
              По предписанию
            </label>
          </span>
        </label>

        <label className={eqFieldLabel}>
          Основание
          <input
            value={form.basis}
            onChange={(e) => setForm((f) => ({ ...f, basis: e.target.value }))}
            className={credInput}
            placeholder="Акт инвентаризации"
          />
        </label>

        <label className={eqFieldLabel}>
          Дата документа
          <input
            type="date"
            value={form.baseDate}
            onChange={(e) => setForm((f) => ({ ...f, baseDate: e.target.value }))}
            className={credInput}
          />
        </label>

        <label className={eqFieldLabel}>
          Номер документа
          <input
            value={form.baseNumber}
            onChange={(e) => setForm((f) => ({ ...f, baseNumber: e.target.value }))}
            className={credInput}
            placeholder="ИНВ-1"
          />
        </label>

        {form.kind === 'instruction' && (
          <>
            <label className={eqFieldLabel}>
              Дата предписания
              <input
                type="date"
                value={form.instructionDate}
                onChange={(e) => setForm((f) => ({ ...f, instructionDate: e.target.value }))}
                className={credInput}
              />
            </label>
            <label className={eqFieldLabel}>
              Номер предписания
              <input
                value={form.instructionNumber}
                onChange={(e) => setForm((f) => ({ ...f, instructionNumber: e.target.value }))}
                className={credInput}
              />
            </label>
            <label className={eqFieldLabel}>
              Налоговый период
              <input
                value={form.taxPeriod}
                onChange={(e) => setForm((f) => ({ ...f, taxPeriod: e.target.value }))}
                className={credInput}
                placeholder="2026"
              />
            </label>
          </>
        )}

        <label className={eqFieldLabel}>
          Сумма, ₽
          <input
            value={form.total}
            onChange={(e) => setForm((f) => ({ ...f, total: e.target.value }))}
            className={credInput}
            inputMode="decimal"
            placeholder="0.00"
          />
        </label>

        <label className={eqFieldLabel}>
          Оплата
          <select
            value={form.paymentMethod}
            onChange={(e) =>
              setForm((f) => ({ ...f, paymentMethod: e.target.value as 'cash' | 'electronic' }))
            }
            className={credInput}
          >
            <option value="cash">Наличные</option>
            <option value="electronic">Безнал</option>
          </select>
        </label>

        <div className="flex items-end gap-2">
          <button className={eqBtn} type="submit" disabled={busy}>
            Пробить чек коррекции
          </button>
          <button className={eqBtn} type="button" onClick={() => void refresh()} disabled={busy}>
            Обновить список
          </button>
        </div>
      </form>

      <h4 className="mb-2 mt-4 text-base2 font-bold">Последние коррекции</h4>
      {items.length === 0 ? (
        <p className={hint}>Чеков коррекции ещё не было.</p>
      ) : (
        <div className={tableWrap}>
          <table className={table}>
            <thead>
              <tr>
                <th className={th}>Дата</th>
                <th className={th}>Признак расчёта</th>
                <th className={thNum}>Сумма</th>
                <th className={th}>Статус</th>
                <th className={th}>ФД / ФПД</th>
                <th className={th}>Кассир</th>
              </tr>
            </thead>
            <tbody>
              {items.map((receipt) => (
                <tr key={receipt.id ?? receipt.receiptId}>
                  <td className={td}>
                    {new Date(receipt.createdAt).toLocaleString('ru-RU')}
                  </td>
                  <td className={td}>
                    {receipt.settlementType
                      ? SETTLEMENT_LABELS[receipt.settlementType]
                      : 'Приход'}
                    {receipt.correction ? ` · ${receipt.correction.kind === 'self' ? 'самостоятельная' : 'по предписанию'}` : ''}
                  </td>
                  <td className={tdNum}>{money(receipt.total)}</td>
                  <td className={td}>
                    {receipt.status === 'fiscalized'
                      ? (<span className="inline-flex items-center gap-1"><Check size={16} className="text-okDark" /> Фискальный чек</span>)
                      : receipt.status === 'offline_pending'
                        ? (<span className="inline-flex items-center gap-1"><Clock size={16} /> Отложенный чек</span>)
                        : 'Уведомление о продаже'}
                  </td>
                  <td className={td}>
                    {receipt.fiscalData
                      ? `ФД ${receipt.fiscalData.fiscalDocumentNumber} · ФПД ${receipt.fiscalData.fiscalDocumentSign}`
                      : '—'}
                  </td>
                  <td className={td}>{receipt.cashierName ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className={hint}>
        Коррекции неизменяемы: из интерфейса их нельзя отредактировать или удалить.
      </p>
    </AdminCard>
  )
}

/** Поле формы коррекции (label + control). */

/** Раздел «Документы»: чеки коррекции (ФЗ-54, ФФД). */
export function DocumentsSection({ core, cashier, perms }: AdminSectionProps) {
  return (
    <CorrectionsSection
      core={core}
      canCorrect={perms.canCorrect}
      cashierName={cashier?.name}
    />
  )
}
